import AddCircleIcon from '@mui/icons-material/AddCircle'
import MenuIcon from '@mui/icons-material/Menu'
import PublishIcon from '@mui/icons-material/Publish'
import SettingsIcon from '@mui/icons-material/Settings'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import { Badge, Button, FormControl, InputLabel, Select } from '@mui/material'
import AppBar from '@mui/material/AppBar'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Website } from 'graphql/types'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import colors from 'utils/colors'
import getEnvConfig from 'utils/getEnvConfig'
import { logout } from 'utils/logout'
import { NavigationRoutes } from 'utils/navigationRoutes'
import { UnpublishedItem } from 'utils/types'
import { getKeycloakInstance } from './AuthWrapper/Auth'

export enum CMSTab {
    PAGES,
    ASSETS,
    DATA,
    TEMPLATE,
    SETTINGS,
    PUBLISH,
    CREATE_WEBSITE,
}

interface Page {
    title: string
    route: NavigationRoutes
    icon?: (props: CMSHeaderProps & { selected: boolean }) => JSX.Element
}

type IconPage = Page & {
    icon: (props: CMSHeaderProps & { selected: boolean }) => JSX.Element
}

const ProfileClick = () => {
    console.log('onProfileClick')
}

export interface CMSHeaderProps {
    unpublishedItems: UnpublishedItem[]
    tabNavigation?: boolean
    websiteList: Website[]
    selectedWebsite: string
    onSelectWebsite: (websiteID: string) => void
}

const iconBtnHeader = { my: 2, color: 'black' }

const CMSHeader = (props: CMSHeaderProps) => {
    const { tabNavigation, selectedWebsite, onSelectWebsite, websiteList } = props

    // const { language, onChangeLanguage } = useLanguage()
    const env = getEnvConfig()
    const navigate = useNavigate()
    const location = useLocation()

    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)

    const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => setAnchorElNav(event.currentTarget)
    const handleCloseNavMenu = () => setAnchorElNav(null)

    const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => setAnchorElUser(event.currentTarget)
    const handleCloseUserMenu = () => setAnchorElUser(null)

    const onClickPage = (page: Page) => page.route !== location.pathname && navigate(page.route)

    const { t } = useTranslation()

    const LogoutClick = async () => {
        if (env.Keycloak.Enabled) {
            const keycloak = getKeycloakInstance()
            keycloak
                .logout({
                    redirectUri: window.location.origin,
                })
                .catch((error) => {
                    console.error('Logout failed:', error)
                })
        } else {
            const token = document.cookie
                .split('; ')
                .find((row) => row.startsWith('token='))
                ?.split('=')[1]
            if (token) {
                await logout(token)
            }
        }
        // call api then reroute alright
    }

    const pages: Page[] = [
        { title: t('components.cmsHeader.pages.pages'), route: NavigationRoutes.PAGE_TREE },
        { title: t('components.cmsHeader.pages.blocks'), route: NavigationRoutes.BLOCK_LIST },
        { title: t('components.cmsHeader.pages.assets'), route: NavigationRoutes.ASSETS },
        { title: t('components.cmsHeader.pages.data'), route: NavigationRoutes.DATA_ITEMS },
    ]

    const iconPages: IconPage[] = [
        {
            title: t('components.cmsHeader.addWebsite'),
            route: NavigationRoutes.WEBSITE_ADD,
            // eslint-disable-next-line react/prop-types
            icon: (props) => <AddCircleIcon style={{ color: props.selected ? colors.primary : colors.black }} />,
        },
        {
            title: t('components.cmsHeader.publish'),
            route: NavigationRoutes.PUBLISH,
            icon: (props) => (
                <Badge
                    badgeContent={props.unpublishedItems.length}
                    sx={{
                        '& .MuiBadge-badge': {
                            // eslint-disable-next-line react/prop-types
                            color: props.selected ? colors.white : colors.black,
                            // eslint-disable-next-line react/prop-types
                            backgroundColor: props.selected ? colors.primary : colors.secondary,
                        },
                    }}
                >
                    <PublishIcon
                        style={{
                            // eslint-disable-next-line react/prop-types
                            color: props.selected ? colors.primary : colors.black,
                        }}
                    />
                </Badge>
            ),
        },

        {
            title: 'AI Live',
            route: NavigationRoutes.AI_LIVE,
            // eslint-disable-next-line react/prop-types
            icon: (props) => <SmartToyIcon style={{ color: props.selected ? colors.primary : colors.black }} />,
        },
        {
            title: t('components.cmsHeader.settings'),
            route: NavigationRoutes.SETTINGS_GENERAL,
            // eslint-disable-next-line react/prop-types
            icon: (props) => <SettingsIcon style={{ color: props.selected ? colors.primary : colors.black }} />,
        },
    ]

    const settings = [
        { name: t('components.cmsHeader.profile'), action: ProfileClick },
        { name: t('components.cmsHeader.logout'), action: LogoutClick },
    ]

    return (
        <>
            <AppBar position="fixed" sx={{ backgroundColor: 'white' }}>
                <Toolbar>
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            flex: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                            maxWidth: 120,
                            paddingRight: 2,
                        }}
                    >
                        <img
                            src={process.env.PUBLIC_URL + '/qubiduLogo.svg'}
                            style={{ maxWidth: 120 }}
                            alt={'Qubidu'}
                            loading="lazy"
                        />
                    </Box>

                    <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                        <Tooltip title="Open navigation">
                            <IconButton size="large" onClick={handleOpenNavMenu} sx={{ color: 'black' }}>
                                <MenuIcon />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{
                                display: { xs: 'block', md: 'none' },
                            }}
                        >
                            {[...pages, ...iconPages].map((page) => (
                                <MenuItem key={page.title} onClick={() => onClickPage(page)}>
                                    <Typography>{page.title}</Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    <Box
                        sx={{
                            flexGrow: 1,
                            display: { xs: 'flex', md: 'none' },
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <img
                            src={process.env.PUBLIC_URL + '/qubiduLogo.svg'}
                            style={{ maxWidth: 90 }}
                            alt={'Qubidu'}
                            loading="lazy"
                        />
                    </Box>
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                        {pages.map((page) => (
                            <Button
                                key={page.title}
                                onClick={() => onClickPage(page)}
                                sx={{
                                    my: 2,
                                    color: page.route === location.pathname ? '#5eafc7' : 'black',
                                }}
                            >
                                <Typography textAlign="center" color="inherit" fontWeight={700}>
                                    {page.title}
                                </Typography>
                            </Button>
                        ))}
                        <Box marginLeft={'auto'} />
                        <FormControl>
                            <InputLabel>{t('components.cmsHeader.website')}</InputLabel>
                            <Select
                                value={selectedWebsite}
                                onChange={(event) => onSelectWebsite(event.target.value)}
                                label={t('components.cmsHeader.website')}
                                style={{ width: '192px', marginRight: '16px' }}
                            >
                                {websiteList.map((website) => (
                                    <MenuItem key={website.id} value={website.id}>
                                        {website.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {/*   <FormControl>
                            <InputLabel>{t('components.cmsHeader.language')}</InputLabel>
                            <Select
                                value={language}
                                onChange={(event) => onChangeLanguage(event.target.value as Language)}
                                label={t('components.cmsHeader.language')}
                                style={{ width: '192px', marginRight: '16px' }}
                            >
                                <MenuItem value={Language.ENGLISH}>{t('components.cmsHeader.english')}</MenuItem>
                                <MenuItem value={Language.GERMAN}>{t('components.cmsHeader.german')}</MenuItem>
                                <MenuItem value={Language.SPANISH}>{t('components.cmsHeader.spanish')}</MenuItem>
                            </Select>
                        </FormControl> */}
                        {iconPages.map((page) => (
                            <Tooltip key={page.title} title={page.title}>
                                <IconButton
                                    color="inherit"
                                    sx={iconBtnHeader}
                                    component="div"
                                    onClick={() => onClickPage(page)}
                                >
                                    {page.icon({ ...props, selected: page.route === location.pathname })}
                                </IconButton>
                            </Tooltip>
                        ))}
                    </Box>

                    <Box sx={{ marginLeft: '16px', flexGrow: 0 }}>
                        <Tooltip title={t('components.cmsHeader.userMenu')} placement="bottom-start">
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                <Avatar alt="Remy Sharp" />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            {settings.map((setting) => (
                                <MenuItem key={setting.name} onClick={handleCloseUserMenu}>
                                    <Typography textAlign="center" onClick={setting.action}>
                                        {setting.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            {!tabNavigation && <div style={{ height: '72px' }}></div>}
        </>
    )
}

export default CMSHeader
